<template>
  <w-modal :value="value" :width="820" content-padding="p-0" @close="close">
    <template #header>
      <h1 class="font-medium text-xl">
        例行性訂購設定
      </h1>
    </template>
    <template #content>
      <div class="setting-container">
        <div class="setting-item">
          <label>訂購週期</label>
          <div class="align-text-bottom gap-2 setting-content">
            <b-radio
              v-model="scheduleType"
              :key="SCHEDULE_TYPES.PERIOD"
              :disabled="viewMode"
              native-value="period"
            >
              間隔天數
            </b-radio>
            <b-radio
              v-model="scheduleType"
              :key="SCHEDULE_TYPES.WEEKLY"
              :disabled="viewMode"
              native-value="weekly"
            >
              每星期幾
            </b-radio>
          </div>
        </div>
        <div v-if="isDayType" class="setting-item">
          <div class="setting-content">
            <label>每隔</label>
            <b-select
              v-model="dayInterval"
              :disabled="viewMode"
              class="select-box"
              @input="v$.$touch"
            >
              <option
                v-for="option in dayIntervalOptions"
                :key="option.label"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </b-select>
            <label>一次</label>
          </div>
        </div>
        <div v-else class="setting-item">
          <div class="setting-content">
            <div class="weeks-section">
              <b-checkbox
                v-for="weekday in weekdayOptions"
                v-model="weekdayIntervals"
                :key="weekday.label"
                :disabled="viewMode"
                :native-value="weekday.value"
                @input="v$.$touch"
              >
                <span>{{ weekday.label }}</span>
              </b-checkbox>
            </div>
          </div>
        </div>
        <div class="light-gray-item rounded-t-sm setting-item">
          <label>週期起始日</label>
          <div class="setting-content">
            {{ receiveDateStartText }}
          </div>
        </div>
        <div class="light-gray-item rounded-b-sm setting-item">
          <label>週期結束日</label>
          <div class="setting-content">
            <b-field class="field-item w-full">
              <b-datepicker
                v-model="receiveDateEnd"
                :date-formatter="formatDate"
                :disabled="viewMode"
                :mobile-native="false"
                :selectable-dates="receiveDateEndSelectableDates"
                append-to-body
                icon="calendar-week"
                locale="zh-TW"
                position="is-top-right"
                @input="v$.$touch"
              />
            </b-field>
          </div>
        </div>
        <div class="setting-divider" />
        <div class="setting-item">
          <label>開放訂購時間</label>
          <div class="setting-content">
            <label>領取日</label>
            <b-select
              v-model="orderStart"
              :disabled="viewMode"
              class="select-box"
              @input="v$.$touch"
            >
              <option
                v-for="option in orderStartOptions"
                :key="option.label"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </b-select>
            <label>開放訂購</label>
          </div>
        </div>
        <div class="setting-item">
          <div class="setting-content">
            <label>每次開放訂購</label>
            <b-select
              v-model="orderPeriodDay"
              :disabled="viewMode"
              class="select-box"
              @input="v$.$touch"
            >
              <option
                v-for="option in orderPeriodDayOptions"
                :key="option.label"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </b-select>
          </div>
        </div>
        <div
          :class="{
            'has-message'
              : v$.orderPeriodTime.start.$error || v$.orderPeriodTime.end.$error
          }"
          class="setting-item"
        >
          <div class="setting-content">
            <label>開放訂購時段</label>
            <label>起日</label>
            <b-field
              :message="v$.orderPeriodTime.start.$errors[0] &&
                v$.orderPeriodTime.start.$errors[0].$message"
              :type="{ 'is-danger': v$.orderPeriodTime.start.$error }"
              class="field-item"
            >
              <b-timepicker
                v-model="orderPeriodTime.start"
                :disabled="viewMode"
                :max-time="orderPeriodTimeRange.start.max"
                :min-time="orderPeriodTimeRange.start.min"
                :mobile-native="false"
                :time-formatter="formatTime"
                append-to-body
                class="time-picker"
                hour-format="12"
                hours-label="小時"
                locale="zh-TW"
                minutes-label="分鐘"
                @input="v$.$touch"
                />
            </b-field>
            <label>至</label>
            <label>訖日</label>
            <b-field
              :message="v$.orderPeriodTime.end.$errors[0] &&
                v$.orderPeriodTime.end.$errors[0].$message"
              :type="{ 'is-danger': v$.orderPeriodTime.end.$error }"
              class="field-item"
            >
              <b-timepicker
                v-model="orderPeriodTime.end"
                :disabled="viewMode"
                :max-time="orderPeriodTimeRange.end.max"
                :min-time="orderPeriodTimeRange.end.min"
                :mobile-native="false"
                :time-formatter="formatTime"
                append-to-body
                class="time-picker"
                hour-format="12"
                hours-label="小時"
                locale="zh-TW"
                minutes-label="分鐘"
                position="is-bottom-left"
                @input="v$.$touch"
                />
            </b-field>
          </div>
        </div>
        <div class="setting-divider" />
        <div class="setting-item mb-4">
          <label>團購公告時間</label>
          <div class="setting-content">
            <label>開放訂購</label>
            <b-select
              v-model="orderCreateDay"
              :disabled="viewMode"
              class="select-box"
              @input="v$.$touch"
            >
              <option
                v-for="option in orderCreateDayOptions"
                :key="option.label"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </b-select>
            <label>公告團購</label>
          </div>
        </div>
        <div class="department light-gray-item mt-4 rounded-sm setting-item">
          <label>可訂購部門</label>
          <div class="setting-content">
            {{ departmentText }}
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex items-center justify-end">
        <w-button
          :disabled="isConfirmBtnDisable || viewMode"
          :loading="loading"
          @click="onConfirm"
        >
          確定
        </w-button>
      </div>
    </template>
  </w-modal>
</template>

<script>
import ScheduleModal from './ScheduleModal';

export default ScheduleModal;
</script>

<style lang="scss" scoped>
@import './ScheduleModal';
</style>
